import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FC } from 'react';
import { observer } from 'mobx-react';
import Output from '../../../components/Output';
import Pipelines from './parts/Pipelines';
import Metrics from './parts/Metrics';
import Search from './parts/search/Search';
import JobsOverview from './parts/JobsOverview';
import Terminate from './parts/Terminate';
import Download from './parts/Download';
import Standalone from './parts/Standalone';
import Ordering from '../../admin/sampleManager/Ordering';
import Tracking from '../../admin/sampleManager/Tracking';
import AutoTriggering from '../../admin/sampleManager/AutoTriggering';
import DataStore from '../../../store';
import { Button, Typography } from '@mui/material';
import { existPageInGroup } from '../../../helpers/groups';
import NotAccess from '../../../components/NotAccess';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

type MenuItem = {
  id: string;
  label: string;
  path: string;
  element: React.ReactElement;
};

const menuItems: MenuItem[] = [
  {
    id: 'search',
    label: 'Search & Process',
    path: '/search',
    element: <Search />,
  },
  { id: 'metrics', label: 'Metrics', path: '/metrics', element: <Metrics /> },
  {
    id: 'pipelines',
    label: 'Pipelines',
    path: '/pipelines',
    element: <Pipelines />,
  },
  {
    id: 'jobsOverview',
    label: 'Jobs Overview',
    path: '/jobs-overview',
    element: <JobsOverview />,
  },
  {
    id: 'terminate',
    label: 'Terminate',
    path: '/terminate',
    element: <Terminate />,
  },
  {
    id: 'download',
    label: 'Download',
    path: '/download',
    element: <Download />,
  },
  {
    id: 'standalone',
    label: 'Standalone',
    path: '/standalone',
    element: <Standalone />,
  },
  {
    id: 'ordering',
    label: 'Ordering',
    path: '/ordering',
    element: <Ordering />,
  },
  {
    id: 'tracking',
    label: 'Tracking',
    path: '/tracking',
    element: <Tracking />,
  },
  {
    id: 'autoTriggering',
    label: 'Auto-Triggering',
    path: '/auto-triggering',
    element: <AutoTriggering />,
  },
];

const Main: FC = observer(() => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const isMobile = useMediaQuery('(max-width:768px)');
  const location = useLocation();
  const navigate = useNavigate();

  const availableItems = menuItems.filter((it) => existPageInGroup(it.id));
  const selectedIndex = availableItems.findIndex(
    (it) => location.pathname === it.path
  );

  const handleDrawerToggle = () => setDrawerOpen((prev) => !prev);

  React.useEffect(() => {
    DataStore.resetPreviewAndProcessing();
  }, [location.pathname]);

  const drawerMenu = (
    <Box sx={{ width: 250 }}>
      <List>
        {menuItems.map((item, index) => {
          if (!existPageInGroup(item.id)) return null;
          return (
            <ListItem key={index} disablePadding>
              <ListItemButton
                selected={availableItems[selectedIndex]?.id === item.id}
                onClick={() => {
                  navigate(item.path);
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Container maxWidth='md'>
      <Box component='section' sx={{ my: 2 }}>
        {isMobile ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
              }}
            >
              <Button
                variant='outlined'
                startIcon={<MenuIcon />}
                onClick={handleDrawerToggle}
                sx={{ width: '100%' }}
              >
                Menu
              </Button>
            </Box>
            <Typography variant='h6' sx={{ marginTop: '15px' }}>
              {availableItems[selectedIndex]?.label ?? ''}
            </Typography>
            <Drawer
              anchor='left'
              open={drawerOpen}
              onClose={handleDrawerToggle}
            >
              {drawerMenu}
            </Drawer>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: '6px',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {menuItems.map((item, index) => {
              if (!existPageInGroup(item.id)) return null;
              return (
                <ListItemButton
                  key={index}
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{ textAlign: 'center', width: 'auto', borderRadius: 1 }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
          </Box>
        )}
      </Box>

      <Box>
        <Routes>
          {menuItems.map((item) => (
            <Route
              key={item.id}
              path={item.path}
              element={
                existPageInGroup(item.id) ? (
                  item.element
                ) : availableItems[0] ? (
                  <Navigate to={availableItems[0].path} replace />
                ) : (
                  <NotAccess onlyContent />
                )
              }
            />
          ))}

          <Route
            path='/'
            element={
              <Navigate to={availableItems[0]?.path ?? '/search'} replace />
            }
          />

          <Route
            path='*'
            element={
              <Navigate to={availableItems[0]?.path ?? '/search'} replace />
            }
          />
        </Routes>
      </Box>

      <Box>
        <Output data={DataStore.output} />
      </Box>
    </Container>
  );
});

export default Main;
